import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, Subject, catchError, map, switchMap, take } from 'rxjs';
import { Modal } from '@spirit-testing/component-library';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { SparklineComponent } from '../../live-dashboard/components/sparkline/sparkline.component';
import { FilterChipComponent } from '../../components/filter-chip/filter-chip.component';
import { CodeIdChipComponent } from '../../components/code-id-chip/code-id-chip.component';
import { SystemBadgeComponent } from '../../components/system-badge/system-badge.component';
import { UmgebungPillComponent } from '../../components/umgebung-pill/umgebung-pill.component';
import { MatrixCellComponent, MatrixStatus } from '../../components/matrix-cell/matrix-cell.component';
import { ResPillComponent, ResPillStatus } from '../../components/res-pill/res-pill.component';
import {
  SegmentedFilterControlComponent,
  SegmentedFilterItem,
} from '../../components/segmented-filter-control/segmented-filter-control.component';
import { TestRunService } from '../../services/test-run.service';
import { SavedViewService } from '../../services/saved-view.service';
import { PageSizePreferenceService } from '../../services/page-size-preference.service';
import { ResultStatus } from '../../models/test-run.model';
import {
  TestResultRow,
  TestResultsSummary,
  TestResultsSummaryQuery,
  TrackerBugRef,
} from '../../models/test-results-summary.model';
import { SavedView } from '../../models/saved-view.model';
import { RobotUploadModalComponent } from './robot-upload-modal/robot-upload-modal.component';
import { ReleaseFreigabeComponent } from './release-freigabe/release-freigabe.component';
import { PlanErgebnisTabelleComponent } from './plan-ergebnis-tabelle/plan-ergebnis-tabelle.component';
import { LaufErgebnisTabelleComponent } from './lauf-ergebnis-tabelle/lauf-ergebnis-tabelle.component';
import { pickCurrentRelease, ReleaseService } from '../../services/release.service';
import { Release } from '../../models/release.model';
import { SavedViewOption, SavedViewsMenuComponent } from '../../shared/saved-views/saved-views-menu.component';
import {
  environmentClass,
  matrixIcon,
  parseResultView,
  ReleaseListState,
  RESULT_VIEWS,
  ResultView,
  SOURCE_SYSTEMS,
  sourceToSystemKey,
  statusKey,
  systemKeyToSource,
  systemMeta,
} from './test-results.types';
import { buildTestResultsSavedViewFiltersJson, parseTestResultsSavedViewFilters } from './test-results.utils';
import { formatDurationSeconds } from '../../shared/duration.util';
import { environmentFilterLabel, isBranchEnvironment } from '../../shared/branch-name';
import { warnBeforeUnload } from '../../shared/forms/warn-before-unload';

interface FilterChip {
  id: ResultStatus | 'all';
  labelKey: string;
  count: number | null;
}

const RESULT_FILTERS: { id: ResultStatus | 'all'; labelKey: string }[] = [
  { id: 'all', labelKey: 'testResults.filter.all' },
  { id: 'PASSED', labelKey: 'testResults.filter.passed' },
  { id: 'FAILED', labelKey: 'testResults.filter.failed' },
  { id: 'BLOCKED', labelKey: 'testResults.filter.blocked' },
  { id: 'SKIPPED', labelKey: 'testResults.filter.skipped' },
];

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const PAGE_SIZE_ROUTE_KEY = 'test-results';
const MAX_DEFECT_KEYS = 4;
const DEFECT_KEYS_BEFORE_MORE = 3;

@Component({
  selector: 'app-test-results',
  imports: [
    TranslatePipe,
    MatIcon,
    MatProgressSpinner,
    ProjectNavComponent,
    PageHeaderComponent,
    SparklineComponent,
    FilterChipComponent,
    CodeIdChipComponent,
    SystemBadgeComponent,
    UmgebungPillComponent,
    MatrixCellComponent,
    ResPillComponent,
    SegmentedFilterControlComponent,
    RouterLink,
    RobotUploadModalComponent,
    SavedViewsMenuComponent,
    ReleaseFreigabeComponent,
    PlanErgebnisTabelleComponent,
    LaufErgebnisTabelleComponent,
    Modal,
  ],
  templateUrl: './test-results.component.html',
  styleUrl: './test-results.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestResultsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private testRunService = inject(TestRunService);
  private savedViewService = inject(SavedViewService);
  private releaseService = inject(ReleaseService);
  private pageSizePref = inject(PageSizePreferenceService);
  private translate = inject(TranslateService);
  private snackBar = inject(MatSnackBar);
  private destroyRef = inject(DestroyRef);

  private readonly snackError: MatSnackBarConfig = { panelClass: 'snackbar-error', duration: 4000 };

  /**
   * Alle Refetches laufen ueber diesen Subject in eine `switchMap`. Damit wird ein
   * frueher Request bei einem schnellen zweiten Klick abgebrochen — Antworten koennen
   * sich nicht mehr ueberholen und die Subscription wird bei Component-Destroy
   * automatisch aufgeraeumt.
   */
  private readonly refetch$ = new Subject<TestResultsSummaryQuery>();

  private readonly currentLang = toSignal(this.translate.onLangChange.pipe(map((e) => e.lang)), {
    initialValue: this.translate.currentLang,
  });

  protected readonly systems = SOURCE_SYSTEMS;
  protected readonly systemMeta = systemMeta;
  protected readonly sourceToSystemKey = sourceToSystemKey;
  protected readonly statusKey = statusKey;
  protected readonly matrixIcon = matrixIcon;
  protected readonly environmentClass = environmentClass;
  protected readonly isBranchEnvironment = isBranchEnvironment;

  protected matrixStatus(status: ResultStatus): MatrixStatus {
    return RESULT_TO_MATRIX_STATUS[status];
  }

  protected resPillStatus(status: ResultStatus): ResPillStatus {
    return RESULT_TO_RES_PILL[status];
  }

  protected readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  protected readonly projectId: string;
  /** Die CI-Zuordnung haengt an einem Testlauf — der Einstieg fuehrt daher in die Laufuebersicht. */
  protected readonly ciMappingLink: string[];

  protected readonly view = signal<ResultView>(parseResultView(this.route.snapshot.queryParamMap.get('sicht')));
  /** Plan-Filter der Sicht „Testläufe" (Query-Parameter `plan`). */
  protected readonly planFilter = signal<string | null>(this.route.snapshot.queryParamMap.get('plan'));

  /** Releases des Projekts; das gewaehlte ist Seitenzustand, den Freigabe-Karte und Sichten teilen. */
  protected readonly releases = signal<Release[]>([]);
  protected readonly selectedReleaseId = signal<string | null>(null);
  protected readonly releasesState = signal<ReleaseListState>('loading');

  private readonly releaseFreigabe = viewChild(ReleaseFreigabeComponent);
  protected readonly showDeactivateConfirm = signal(false);
  private readonly deactivateConfirm$ = new Subject<boolean>();

  protected readonly releaseEnvironment = computed(() => (this.envFilter() === 'all' ? null : this.envFilter()));
  protected readonly releaseSource = computed(() =>
    this.systemFilter() === 'all' ? null : systemKeyToSource(this.systemFilter()),
  );

  protected readonly viewItems = computed<SegmentedFilterItem[]>(() => {
    this.currentLang();
    return RESULT_VIEWS.map((v) => ({
      id: v.id,
      label: this.translate.instant(`testResults.views.${v.id}.label`),
      icon: v.icon,
      testId: `ergebnis-tab-${v.id}`,
    }));
  });

  protected readonly loading = signal(true);
  protected readonly refetching = signal(false);
  protected readonly loadError = signal(false);
  private readonly summary = signal<TestResultsSummary | null>(null);

  protected readonly envFilter = signal<string>('all');
  protected readonly systemFilter = signal<string>('all');
  protected readonly resultFilter = signal<ResultStatus | 'all'>('all');

  protected readonly pageIndex = signal(0);
  protected readonly pageSize = signal(
    this.pageSizePref.get(PAGE_SIZE_ROUTE_KEY, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE),
  );

  protected readonly showUploadModal = signal(false);

  protected readonly savedViews = signal<SavedView[]>([]);
  protected readonly activeSavedViewName = signal<string | null>(null);
  protected readonly savedViewsError = signal<string | null>(null);

  protected readonly savedViewsOptions = computed<SavedViewOption[]>(() =>
    this.savedViews().map((v) => ({ name: v.name, summary: this.summarizeSavedView(v) })),
  );

  protected readonly rows = computed(() => this.summary()?.rows ?? []);
  protected readonly environments = computed(() => this.summary()?.environments ?? []);
  protected readonly trend = computed(() => this.summary()?.passRateTrend ?? []);

  protected readonly passRate = computed(() => Math.round(this.summary()?.passRate ?? 0));
  protected readonly executed = computed(() => this.summary()?.executed ?? 0);
  protected readonly passed = computed(() => this.summary()?.passed ?? 0);
  protected readonly failed = computed(() => this.summary()?.failed ?? 0);
  protected readonly openSkipped = computed(() => this.summary()?.openSkipped ?? 0);
  protected readonly blocked = computed(() => this.summary()?.blocked ?? 0);
  protected readonly skipped = computed(() => this.summary()?.skipped ?? 0);

  protected readonly totalElements = computed(() => this.summary()?.totalElements ?? 0);
  protected readonly pageCount = computed(() => Math.max(1, Math.ceil(this.totalElements() / this.pageSize())));
  protected readonly rangeStart = computed(() =>
    this.totalElements() === 0 ? 0 : this.pageIndex() * this.pageSize() + 1,
  );
  protected readonly rangeEnd = computed(() =>
    Math.min(this.totalElements(), (this.pageIndex() + 1) * this.pageSize()),
  );

  protected readonly barWidths = computed(() => {
    const total = this.executed();
    if (total === 0) {
      return { passed: 0, failed: 0, openSkipped: 0 };
    }
    return {
      passed: (this.passed() / total) * 100,
      failed: (this.failed() / total) * 100,
      openSkipped: (this.openSkipped() / total) * 100,
    };
  });

  protected readonly filterChips = computed<FilterChip[]>(() => {
    const counts: Partial<Record<FilterChip['id'], number>> = {
      all: this.executed(),
      PASSED: this.passed(),
      FAILED: this.failed(),
      BLOCKED: this.blocked(),
      SKIPPED: this.skipped(),
    };
    return RESULT_FILTERS.map((f) => ({ ...f, count: counts[f.id] ?? null }));
  });

  // Der geteilte Segmented-Control setzt einen Roving-Tabindex: der aktive
  // Button bekommt `tabindex=0`, alle anderen `-1`. Passt kein Item zum
  // aktuellen envFilter(), ist die ganze Gruppe nicht mehr per Tastatur
  // erreichbar (WCAG 2.1.1). Ein gespeicherter View kann einen Wert liefern,
  // der nach dem Loeschen der Umgebung nicht mehr in environments() steht —
  // wir haengen ihn dann als Orphan-Item an, damit genau ein Button aktiv
  // bleibt und der Nutzer sieht, wonach gefiltert wird (sgraetz-Review C1).
  protected readonly envSegmentedItems = computed<SegmentedFilterItem[]>(() => {
    this.currentLang();
    const envs = this.environments();
    const active = this.envFilter();
    const items: SegmentedFilterItem[] = [
      { id: 'all', label: this.translate.instant('testResults.env.all') },
      ...envs.map((env) => ({ id: env, label: environmentFilterLabel(env) })),
    ];
    if (active !== 'all' && !envs.includes(active)) {
      items.push({ id: active, label: environmentFilterLabel(active) });
    }
    return items;
  });

  protected readonly systemOptions = computed(() => {
    this.currentLang();
    return SOURCE_SYSTEMS.map((s) => ({
      key: s.key,
      deferred: s.deferred,
      label:
        this.translate.instant(s.labelKey) +
        (s.deferred ? this.translate.instant('testResults.system.deferredSuffix') : ''),
    }));
  });

  constructor() {
    warnBeforeUnload(() => this.hasUnsavedChanges());
    this.projectId = this.route.snapshot.paramMap.get('projectId') ?? '';
    this.ciMappingLink = ['/projects', this.projectId, 'test-runs'];
    // Vor/Zurueck im Browser aendert nur die Query-Parameter — die Sicht folgt der URL.
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.view.set(parseResultView(params.get('sicht')));
      this.planFilter.set(params.get('plan'));
    });
    // Einzige Subscription — `switchMap` verwirft laufende Requests, wenn ein neuer
    // eintrifft, und `catchError` verhindert, dass ein Fehler die Kette killt.
    this.refetch$
      .pipe(
        switchMap((query) =>
          this.testRunService.getResultsSummary(this.projectId, query).pipe(
            catchError(() => {
              this.handleLoadError();
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((summary) => this.handleLoadSuccess(summary));
  }

  /** Ungespeichert kann hier nur der Entwurf der Freigabe-Bewertung sein. */
  hasUnsavedChanges(): boolean {
    return this.releaseFreigabe()?.hasUnsavedChanges() ?? false;
  }

  confirmDeactivate(): Observable<boolean> {
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  protected onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  protected onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }

  ngOnInit(): void {
    this.loadSavedViews();
    this.loadReleases();
  }

  /** Vorauswahl: das aktuelle Release in Abnahme, sonst das erste der Liste (Klaerung NEX-1452). */
  private loadReleases(): void {
    this.releaseService.list(this.projectId).subscribe({
      next: (releases) => {
        this.releases.set(releases);
        this.selectedReleaseId.set((pickCurrentRelease(releases) ?? releases[0])?.id ?? null);
        this.releasesState.set('loaded');
        this.reload();
      },
      error: () => {
        this.releases.set([]);
        this.releasesState.set('error');
        this.reload();
      },
    });
  }

  /** Ein Release-Wechsel verwirft den Entwurf der Freigabe-Bewertung, daher dieselbe Rückfrage wie beim Verlassen. */
  protected onReleaseChange(releaseId: string): void {
    if (!this.hasUnsavedChanges()) {
      this.applyReleaseChange(releaseId);
      return;
    }
    this.confirmDeactivate()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((discard) => {
        if (discard) this.applyReleaseChange(releaseId);
      });
  }

  /** Ein Plan-Filter gilt nur im Release, aus dem er stammt. */
  private applyReleaseChange(releaseId: string): void {
    this.selectedReleaseId.set(releaseId);
    if (this.planFilter() !== null) this.clearPlanFilter();
    this.pageIndex.set(0);
    this.reload();
  }

  protected clearPlanFilter(): void {
    this.planFilter.set(null);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { plan: null },
      queryParamsHandling: 'merge',
    });
  }

  /** Aktueller Filter-/Page-Stand → Server-Refetch. */
  private reload(): void {
    // Initial-Load zeigt den Full-Page-Spinner; Folge-Refetches lassen den letzten guten
    // Stand stehen, damit ein einzelner 500er oder Netzaussetzer nicht die ganze Seite
    // in einen Fehler-Endzustand zwingt.
    if (this.summary() === null) {
      this.loading.set(true);
    } else {
      this.refetching.set(true);
    }
    this.refetch$.next(this.buildQuery());
  }

  private buildQuery(): TestResultsSummaryQuery {
    const env = this.envFilter();
    const system = this.systemFilter();
    const result = this.resultFilter();
    return {
      page: this.pageIndex(),
      size: this.pageSize(),
      environment: env === 'all' ? undefined : env,
      source: system === 'all' ? undefined : (systemKeyToSource(system) ?? undefined),
      result: result === 'all' ? undefined : result,
      releaseId: this.selectedReleaseId() ?? undefined,
    };
  }

  private handleLoadSuccess(summary: TestResultsSummary): void {
    this.summary.set(summary);
    this.loadError.set(false);
    this.loading.set(false);
    this.refetching.set(false);
  }

  private handleLoadError(): void {
    this.loading.set(false);
    this.refetching.set(false);
    if (this.summary() === null) {
      // Kein guter Stand vorhanden → Full-Page-Fehler (Initial-Load).
      this.loadError.set(true);
      return;
    }
    // Guter Stand vorhanden → letzten Stand stehen lassen und Snackbar anzeigen.
    this.snackBar.open(this.translate.instant('testResults.refetchError'), undefined, this.snackError);
  }

  private loadSavedViews(): void {
    this.savedViewService.list(this.projectId, 'TEST_RESULTS').subscribe({
      next: (views) => this.savedViews.set(views),
      error: () => this.savedViews.set([]),
    });
  }

  private summarizeSavedView(view: SavedView): string {
    const filters = parseTestResultsSavedViewFilters(view.filtersJson);
    const parts = [
      filters.env !== 'all' ? filters.env : null,
      filters.system !== 'all' ? this.translate.instant(`testResults.system.${filters.system}`) : null,
      filters.result !== 'all' ? this.translate.instant(`testResults.result.${statusKey(filters.result)}`) : null,
    ].filter((s): s is string => !!s);
    if (parts.length === 0) {
      return this.translate.instant('testResults.savedViews.summaryAll');
    }
    return parts.join(' · ');
  }

  protected onSavedViewSelected(name: string): void {
    const view = this.savedViews().find((v) => v.name === name);
    if (!view) return;
    const filters = parseTestResultsSavedViewFilters(view.filtersJson);
    // Alle drei Filter in einem Zug setzen und danach EINEN Refetch triggern —
    // damit nicht bei jedem Set ein eigener API-Call ausgeloest wird.
    this.envFilter.set(filters.env);
    this.systemFilter.set(filters.system);
    this.resultFilter.set(filters.result);
    this.pageIndex.set(0);
    this.activeSavedViewName.set(view.name);
    this.reload();
  }

  protected onSavedViewSaved(name: string): void {
    const filtersJson = buildTestResultsSavedViewFiltersJson({
      env: this.envFilter(),
      system: this.systemFilter(),
      result: this.resultFilter(),
    });
    this.savedViewService.upsert(this.projectId, { name, context: 'TEST_RESULTS', filtersJson }).subscribe({
      next: (saved) => {
        this.savedViews.update((list) => {
          const rest = list.filter((v) => v.name !== saved.name);
          return [...rest, saved].sort((a, b) => a.name.localeCompare(b.name));
        });
        this.activeSavedViewName.set(saved.name);
        this.savedViewsError.set(null);
      },
      error: () => this.savedViewsError.set(this.translate.instant('testResults.savedViews.saveError')),
    });
  }

  /**
   * Das Loeschen selbst uebernimmt der generische SavedViewsMenuComponent (NEX-786) —
   * hier wird nur noch der lokale State nach einem erfolgreichen Loeschvorgang bereinigt.
   */
  protected onSavedViewDeleted(name: string): void {
    this.savedViews.update((list) => list.filter((v) => v.name !== name));
    if (this.activeSavedViewName() === name) {
      this.activeSavedViewName.set(null);
    }
  }

  protected onSavedViewDeleteFailed(): void {
    this.savedViewsError.set(this.translate.instant('testResults.savedViews.deleteError'));
  }

  private clearActiveView(): void {
    if (this.activeSavedViewName() !== null) {
      this.activeSavedViewName.set(null);
    }
  }

  protected openUpload(): void {
    this.showUploadModal.set(true);
  }

  protected onImported(): void {
    // Reload mit aktuellem Filter/Page-Stand — der `summary()`-Check in reload() haelt den
    // Full-Page-Spinner ohnehin unten, sobald bereits einmal Daten geladen wurden.
    this.reload();
  }

  protected setView(id: string | null): void {
    const next = parseResultView(id);
    if (next === this.view()) return;
    const plan = next === 'laeufe' ? this.planFilter() : null;
    this.view.set(next);
    this.planFilter.set(plan);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sicht: next, plan },
      queryParamsHandling: 'merge',
    });
  }

  /** „Testläufe des Plans" aus der Sicht „Testpläne": Sicht „Testläufe" mit Plan-Filter. */
  protected openPlanLaeufe(planKey: string | null): void {
    this.view.set('laeufe');
    this.planFilter.set(planKey);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sicht: 'laeufe', plan: planKey },
      queryParamsHandling: 'merge',
    });
  }

  protected setEnv(env: string | null): void {
    const next = env ?? 'all';
    if (this.envFilter() === next) return;
    this.envFilter.set(next);
    this.pageIndex.set(0);
    this.clearActiveView();
    this.reload();
  }

  protected setSystem(system: string): void {
    if (this.systemFilter() === system) return;
    this.systemFilter.set(system);
    this.pageIndex.set(0);
    this.clearActiveView();
    this.reload();
  }

  protected setResult(result: ResultStatus | 'all'): void {
    if (this.resultFilter() === result) return;
    this.resultFilter.set(result);
    this.pageIndex.set(0);
    this.clearActiveView();
    this.reload();
  }

  protected onPageChange(index: number): void {
    if (index < 0 || index >= this.pageCount() || index === this.pageIndex()) {
      return;
    }
    this.pageIndex.set(index);
    this.reload();
  }

  protected onPageSizeChange(size: number): void {
    if (!size || size === this.pageSize()) {
      return;
    }
    this.pageSize.set(size);
    this.pageIndex.set(0);
    this.pageSizePref.set(PAGE_SIZE_ROUTE_KEY, size, PAGE_SIZE_OPTIONS);
    this.reload();
  }

  protected isDeferred(row: TestResultRow): boolean {
    return systemMeta(sourceToSystemKey(row.source)).deferred;
  }

  protected formatDuration(seconds: number | null): string {
    return formatDurationSeconds(seconds);
  }

  /**
   * RouterLink target of the row: the test case's run history (NEX-731), the drilldown
   * from the overview. Null for deferred sources, which are shown but not navigable.
   */
  protected rowLink(row: TestResultRow): unknown[] | null {
    if (this.isDeferred(row)) {
      return null;
    }
    return ['/projects', this.projectId, 'test-results', 'history', row.testCaseId];
  }

  protected visibleDefects(row: TestResultRow): TrackerBugRef[] {
    const defects = this.defectEntries(row);
    return defects.length > MAX_DEFECT_KEYS ? defects.slice(0, DEFECT_KEYS_BEFORE_MORE) : defects;
  }

  protected hiddenDefects(row: TestResultRow): TrackerBugRef[] {
    return this.defectEntries(row).slice(this.visibleDefects(row).length);
  }

  protected defectKeys(defects: TrackerBugRef[]): string {
    return defects.map((d) => d.key).join(', ');
  }

  private defectEntries(row: TestResultRow): TrackerBugRef[] {
    const defects = row.defects ?? [];
    const trackerBugs = row.trackerBugs ?? [];
    const trackerKeys = new Set(trackerBugs.map((t) => t.key));
    return [
      ...trackerBugs.map((t) => ({
        key: t.key,
        blocking: t.blocking || defects.some((d) => d.key === t.key && d.blocking),
      })),
      ...defects.filter((d) => !trackerKeys.has(d.key)).map((d) => ({ key: d.key, blocking: d.blocking })),
    ];
  }

  protected defectsDescriptionId(row: TestResultRow): string {
    return `tr-defects-${row.testCaseId}`;
  }

  /** Die Zeile trägt ein eigenes aria-label; die Defects kommen daher als Beschreibung dazu. */
  protected defectsDescription(row: TestResultRow): string {
    const defects = this.defectEntries(row);
    const blocking = this.translate.instant('testResults.defects.blocking');
    const items = defects.length
      ? defects.map((d) => (d.blocking ? `${d.key} (${blocking})` : d.key)).join(', ')
      : this.translate.instant('testResults.defects.none');
    return `${this.translate.instant('testResults.table.defects')}: ${items}`;
  }

  /** Concise aggregated label so screen readers announce the row instead of every cell. */
  protected rowAriaLabel(row: TestResultRow): string {
    return this.translate.instant('testResults.rowAriaLabel', {
      key: row.testCaseKey ?? '',
      title: row.title ?? '',
      result: this.translate.instant('testResults.result.' + statusKey(row.lastResult)),
    });
  }
}

const RESULT_TO_MATRIX_STATUS: Record<ResultStatus, MatrixStatus> = {
  PASSED: 'passed',
  FAILED: 'failed',
  BLOCKED: 'blocked',
  RUNNING: 'running',
  SKIPPED: 'skipped',
  NOT_RUN: 'not-run',
};

const RESULT_TO_RES_PILL: Record<ResultStatus, ResPillStatus> = {
  PASSED: 'passed',
  FAILED: 'failed',
  BLOCKED: 'blocked',
  RUNNING: 'running',
  SKIPPED: 'skipped',
  NOT_RUN: 'not-run',
};
